// Dev-only mock for cashier URL / version detection.
//
// On localhost the config page can't read the cross-origin cashier iframe, so
// the real V2/V3 detection (Configs.vue -> setNewCashierVersion) never runs.
// This lets you simulate the url the backend would have resolved, so the whole
// downstream behaviour (URL page, version tooltip, Theme V2 locking) can be
// exercised locally. It is a no-op in production builds.
//
// How to set a mock (any of these, development only):
//   - Console helper:  setMockCashierVersion('v3/staging')  // best DX, re-runs
//                      setMockCashierVersion('master')      // detection live
//                      setMockCashierVersion(null)          // clear the mock
//   - localStorage:   localStorage.setItem('mockCashierVersion', 'master')
//                     (then reload or change the merchantId)
//   - Query param:    ?mockCashierVersion=v3/staging
//                     Note: the local dev-server SPA fallback can strip the
//                     query string, so prefer the console helper / localStorage
//                     locally. The query param is honoured wherever the path is
//                     served intact (e.g. a deployed build).
//
// The version shorthand accepts anything the backend serves, e.g.
//   master | develop | 1.2.3          -> v2 (Vue)
//   v3/staging | v3/main | v3/my-fix  -> v3 (React)

const VERSION_KEY = 'mockCashierVersion'
const URL_KEY = 'mockCashierUrl'
const CHECKOUT_BASE = 'https://checkout.paymentiq.io/cashier'

const isDev = () => process.env.NODE_ENV !== 'production'

const versionToUrl = (version) =>
  `${CHECKOUT_BASE}/${version.replace(/^\/+|\/+$/g, '')}/`

/**
 * @returns { string | null } the mocked resolved cashier url, or null when no
 * mock is configured or when running in production.
 */
export const getMockCashierUrl = () => {
  if (!isDev() || typeof window === 'undefined') {
    return null
  }
  try {
    const params = new URLSearchParams(window.location.search)
    const paramUrl = params.get(URL_KEY)
    if (paramUrl) {
      return paramUrl
    }
    const paramVersion = params.get(VERSION_KEY)
    if (paramVersion) {
      return versionToUrl(paramVersion)
    }
    const storedUrl = localStorage.getItem(URL_KEY)
    if (storedUrl) {
      return storedUrl
    }
    const storedVersion = localStorage.getItem(VERSION_KEY)
    if (storedVersion) {
      return versionToUrl(storedVersion)
    }
  } catch (err) {
    console.log(err)
  }
  return null
}

/**
 * Exposes window.setMockCashierVersion(version) in development for quick
 * switching from the console. Passing a falsy value clears the mock.
 * @param { Function } onChange // called after the mock changes so the caller
 * can re-run detection.
 */
export const installMockCashierHelper = (onChange) => {
  if (!isDev() || typeof window === 'undefined') {
    return
  }
  window.setMockCashierVersion = (version) => {
    try {
      if (version) {
        localStorage.setItem(VERSION_KEY, version)
        localStorage.removeItem(URL_KEY)
      } else {
        localStorage.removeItem(VERSION_KEY)
        localStorage.removeItem(URL_KEY)
      }
    } catch (err) {
      console.log(err)
    }
    const url = getMockCashierUrl()
    console.log(`[mock] cashier url -> ${url || '(cleared)'}`)
    if (typeof onChange === 'function') {
      onChange()
    }
    return url
  }
}
